import type * as React from 'react'
import { PureComponent } from 'react'
import { FormattedMessage } from 'react-intl'
import type { useLocation, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { E2E_ERROR } from 'Constants/e2e'
import { SentryErrorType } from 'Constants/errors'

interface InjectedProps {
  location: ReturnType<typeof useLocation>
  navigate: ReturnType<typeof useNavigate>
  children?: React.ReactNode
}

interface State {
  hasError: boolean
}

export class ErrorBoundary extends PureComponent<InjectedProps, State> {
  constructor(props: Readonly<InjectedProps>) {
    super(props)

    this.state = {
      hasError: false,
    }
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return {
      hasError: true,
    }
  }

  componentDidUpdate(prevProps: InjectedProps) {
    const { location } = this.props
    const { hasError } = this.state

    // Clear error on navigation
    if (location.pathname !== prevProps.location.pathname && hasError) {
      this.setState({ hasError: false })
    }

    if (location !== prevProps.location) {
      window.Intercom('update', {
        hide_default_launcher: false,
      })
    }
  }

  componentDidCatch(
    error: Error,
  ): ReturnType<NonNullable<PureComponent['componentDidCatch']>> {
    Sentry.captureException(error, {
      extra: {
        location: window?.location || undefined,
        type: SentryErrorType.COMPONENT_CRASH,
      },
    })
  }

  render(): ReturnType<PureComponent['render']> {
    const { children } = this.props
    const { hasError } = this.state

    if (hasError) {
      return (
        <div
          className="d-flex align-items-center justify-content-center h-100"
          data-e2e={E2E_ERROR}
        >
          <span className="text-center text-muted">
            <span>
              <FormattedMessage id="Error_occurred" />
            </span>
            <br />
            <span>
              <FormattedMessage id="Error_refresh_page" />
            </span>
          </span>
        </div>
      )
    }

    return children
  }
}
